import { memo, useEffect, useMemo, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useAtomValue, useSetAtom } from 'jotai';
import { BriefcaseIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { Button } from '@restworld/ui-ds-base';
import { CUSTOM_EMPLOYER_BASE_URL, cn } from '@restworld/utils-common';

import { ModalWrapper } from '../common/modal-wrapper';
import { AuthTitle } from '../common/auth-title';
import { AuthSubtitle } from '../common/auth-subtitle';
import { isSocialUserTypeModalOpen, closeSocialUserTypeModal, socialSignupDetails } from '../../context/auth-flow';
import { openStandaloneUploadCvEntryPoint } from '../worker-entry-point/worker-entry-point-modal';

type UserType = 'worker' | 'employer';

// Pre-select a hint from the route the person came from, but never as a default
// they can't change: an ambiguous route leaves nothing selected.
function detectUserTypeFromRoute(pathname: string): UserType | null {
  if (/\/(aziende|employer|getting-started)/.test(pathname)) return 'employer';
  if (/\/(cerco-lavoro|lavoratori)/.test(pathname)) return 'worker';
  return null;
}

/**
 * Shown only for a NEW Google user (backend `registered: false`). Asks whether
 * they are looking for work or hiring staff, then routes them to the matching
 * signup: workers to `/cerco-lavoro` with the CV upload open, employers to the
 * employer app's `/getting-started` (carrying only email/name as prefill; the
 * `google_id` is linked later on the user's next verified Google sign-in).
 */
export const SocialUserTypeModal = memo(() => {
  const { t } = useTranslation('auth-flow');
  const router = useRouter();

  const open = useAtomValue(isSocialUserTypeModalOpen);
  const closeModal = useSetAtom(closeSocialUserTypeModal);
  const details = useAtomValue(socialSignupDetails);
  const setSocialDetails = useSetAtom(socialSignupDetails);
  const openUploadCvEntryPoint = useSetAtom(openStandaloneUploadCvEntryPoint);

  const routeHint = useMemo(() => detectUserTypeFromRoute(router.asPath || ''), [router.asPath]);
  const [selected, setSelected] = useState<UserType | null>(routeHint);

  // Reset the selection to the route hint every time the modal opens.
  useEffect(() => {
    if (open) setSelected(detectUserTypeFromRoute(router.asPath || ''));
  }, [open, router.asPath]);

  function onContinue() {
    if (!selected) return;
    closeModal();

    if (selected === 'worker') {
      // Open the CV-upload step via the app-wide standalone entry point, which
      // renders on any page. Flipping entryPointModalVisibleAtom alone opens
      // nothing unless the current page already mounts an instance (e.g. the nav
      // side menu), so this is the reliable opener. Only redirect to
      // /cerco-lavoro when we are not already there, as the landing behind the
      // modal; if we already are, a re-push would be a pointless navigation.
      if (router.pathname.startsWith('/cerco-lavoro')) {
        openUploadCvEntryPoint();
      } else {
        router.push('/cerco-lavoro').then(() => openUploadCvEntryPoint());
      }
      return;
    }

    // Employer signup lives in the separate employer app (different origin), so the profile
    // is passed via the URL rather than shared jotai state. Open it in a new tab
    // (noopener/noreferrer) so the worker-app login tab is left intact behind it.
    //
    // Only email + name travel, as form prefill. google_id is deliberately NOT forwarded: the
    // employer app would write it into the org-creation payload unverified (the backend only
    // proves Google identity from the id_token on the login/upload paths, never here). It gets
    // linked safely on the user's next Google sign-in, which the backend stamps by verified email.
    const params = new URLSearchParams();
    if (details.email) params.set('email', details.email);
    if (details.name) params.set('name', details.name);
    const employerSignupUrl = `${CUSTOM_EMPLOYER_BASE_URL}/getting-started?${params.toString()}`;
    window.open(employerSignupUrl, '_blank', 'noopener,noreferrer');
    // The employer app now has the profile in its URL; drop the worker-app copy so
    // it can't later attach to a CV upload in this tab.
    setSocialDetails({ email: null, name: null, google_id: null, id_token: null });
  }

  // Dismissed without choosing: drop the pending Google profile so it can't attach
  // to an unrelated CV upload later on this browser.
  function handleDismiss() {
    setSocialDetails({ email: null, name: null, google_id: null, id_token: null });
    closeModal();
  }

  return (
    <ModalWrapper
      open={open}
      onClose={handleDismiss}
      // Fixed header + footer; only the cards between them scroll.
      title={
        // px-16 on mobile keeps the centered title/subtitle clear of the absolutely
        // positioned close X (right-4, a ~42px IconButton), which otherwise overlapped
        // the subtitle on a narrow drawer. md:px-8 lines the header text up with the
        // body/cards (px-8) on the wider desktop dialog, where the X never overlaps.
        <div className="flex flex-col items-center gap-2 w-full text-center px-16 md:px-8">
          <AuthTitle>{t('social_user_type_modal_title')}</AuthTitle>
          <AuthSubtitle>{t('social_user_type_modal_subtitle')}</AuthSubtitle>
        </div>
      }
      footer={
        <Button fullwidth={true} disabled={!selected} onClick={onContinue}>
          {t('social_user_type_continue')}
        </Button>
      }
    >
      <div className="grid grid-cols-2 gap-3 w-full py-4">
        <OptionCard
          aria-pressed={selected === 'worker'}
          selected={selected === 'worker'}
          onClick={() => setSelected('worker')}
        >
          <IconBadge>
            <BriefcaseIcon className="w-6 h-6" />
          </IconBadge>
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-gray-900">{t('social_user_type_worker')}</span>
            <span className="text-sm text-gray-500">{t('social_user_type_worker_subtitle')}</span>
          </div>
        </OptionCard>

        <OptionCard
          aria-pressed={selected === 'employer'}
          selected={selected === 'employer'}
          onClick={() => setSelected('employer')}
        >
          <IconBadge>
            <UsersIcon className="w-6 h-6" />
          </IconBadge>
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-gray-900">{t('social_user_type_employer')}</span>
            <span className="text-sm text-gray-500">{t('social_user_type_employer_subtitle')}</span>
          </div>
        </OptionCard>
      </div>
    </ModalWrapper>
  );
});

// No ui-ds-base equivalent for a selectable option card (Badge is a pill, Button
// is an action), so a plain button with Tailwind classes is used. Mirrors the
// employer "Che tipo di azienda hai?" cards: icon badge + title + subtitle.
const OptionCard = ({
  selected,
  className,
  ...props
}: { selected: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    type="button"
    className={cn(
      'flex flex-col items-center text-center gap-3 px-4 py-6 rounded-2xl border transition-colors',
      selected ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-gray-300',
      className
    )}
    {...props}
  />
);

const IconBadge = ({ children }: { children: ReactNode }) => (
  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 text-purple-600">
    {children}
  </div>
);
